import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin, Youtube, ArrowRight } from "lucide-react";
import { BRAND, NAV_LINKS, DESTINATIONS } from "../mock";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import api from "../lib/api";

export default function Footer() {
  const [primary, setPrimary] = useState(null);

  useEffect(() => {
    api.get("/public/companies")
      .then(({ data }) => {
        if (Array.isArray(data) && data.length) {
          setPrimary(data.find((c) => c.show_on_header) || data[0]);
        }
      })
      .catch(() => {});
  }, []);

  const name = primary?.name || BRAND.legal;
  const address = primary?.address || BRAND.address;
  const phone = primary?.phone || BRAND.phone;
  const email = primary?.email || BRAND.email;

  return (
    <footer className="bg-[#013d2b] text-white/80 mt-0">
      {/* CTA strip */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl md:text-3xl text-white font-bold">Ready to study overseas?</h3>
            <p className="mt-2 text-white/70">Book a free 30-minute consult and leave with a real shortlist, not a brochure.</p>
          </div>
          <Link to="/contact">
            <Button className="bg-[#c9a227] hover:bg-[#e0c05a] text-[#013d2b] rounded-full h-12 px-8 font-bold shadow-lg">
              Book a Free Consult <ArrowRight size={18} className="ml-1" />
            </Button>
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="GDIH" className="h-14 w-auto bg-white/95 rounded-lg p-1" />
            <div className="leading-tight">
              <p className="font-display text-white font-bold text-lg">GDIH Elite</p>
              <p className="text-[10px] tracking-[0.2em] text-[#e0c05a] uppercase font-semibold">Education</p>
            </div>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-white/65">
            {name}. Your trusted partner for world-class education abroad — counselling, admissions, visas and scholarships.
          </p>
          <div className="mt-6">
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold">Our Group Company</p>
            <div className="mt-2 bg-white rounded-xl px-3 py-2 inline-flex items-center" data-testid="group-company-logo">
              <img src="/oc-group-logo.png" alt="Overseas Consultants" className="h-8 w-auto" />
            </div>
            <p className="text-xs text-white/50 mt-2">Managing education across India.</p>
          </div>
          <div className="flex gap-3 mt-5">
            {[Facebook, Instagram, Linkedin, Youtube].map((Icon, i) => (
              <a key={i} href="#" className="w-9 h-9 grid place-items-center rounded-full bg-white/10 hover:bg-[#c9a227] hover:text-[#013d2b] transition-colors">
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4">Explore</h4>
          <ul className="space-y-2.5 text-sm">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="hover:text-[#e0c05a] transition-colors">{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4">Top Destinations</h4>
          <ul className="space-y-2.5 text-sm">
            {DESTINATIONS.slice(0, 6).map((d) => (
              <li key={d.slug}>
                <Link to={`/destinations/${d.slug}`} className="hover:text-[#e0c05a] transition-colors">{d.flag} {d.name}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4">Get in touch</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex gap-3" data-testid="footer-address"><MapPin size={17} className="text-[#e0c05a] shrink-0 mt-0.5" /> {address}</li>
            <li className="flex gap-3 items-center"><Phone size={16} className="text-[#e0c05a] shrink-0" /> <a href={`tel:${phone}`} data-testid="footer-phone" className="hover:text-[#e0c05a] transition-colors">{phone}</a></li>
            <li className="flex gap-3 items-center"><Mail size={16} className="text-[#e0c05a] shrink-0" /> <a href={`mailto:${email}`} data-testid="footer-email" className="hover:text-[#e0c05a] transition-colors">{email}</a></li>
          </ul>
          <div className="mt-5">
            <p className="text-xs mb-2 text-white/60">Subscribe for admission updates</p>
            <div className="flex gap-2">
              <Input placeholder="Your email" className="bg-white/10 border-white/20 text-white placeholder:text-white/40 h-10" />
              <Button className="bg-[#c9a227] hover:bg-[#e0c05a] text-[#013d2b] h-10 px-4 font-semibold">Join</Button>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-5 space-y-3 text-xs text-white/55">
          <p className="leading-relaxed text-white/45" data-testid="footer-disclaimer">
            Disclaimer: GDIH Elite Education acts as an independent education consultancy and application facilitator.
            All admission, scholarship, and visa decisions rest with the respective institutions and authorities.
            Programme and fee details are set by third parties and may change without notice. An enquiry or application
            does not guarantee admission, a scholarship, a fee waiver, or a visa.
          </p>
          <div className="flex flex-col md:flex-row justify-between items-center gap-3">
            <p>© {new Date().getFullYear()} {name}. All rights reserved.</p>
            <div className="flex gap-5">
              <Link to="/privacy" className="hover:text-white/80" data-testid="footer-privacy-link">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white/80" data-testid="footer-terms-link">Terms &amp; Conditions</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}